import { normalizeCustomDisplayName, sectorNameKey } from '../soverdi-clone/adapters/sectorNaming';
import i18n, { t } from 'i18next';
// SOVERDI view-model adapter. No runtime, command or activation endpoint.
import { iotApi, type ParcelValveBinding } from '../lib/iotApi';

export type ProgramType = 'irrigation' | 'fertigation';
export type RecurrenceType = 'once' | 'daily' | 'weekly' | 'cyclic';
export interface ProgrammableSector {
  id: string; name: string; culture?: string; surface_ha?: number;
  valveIds: string[]; equipmentOk: boolean;
}
export interface ProgramStep {
  id: string; order: number; sectorId: string; sectorName: string;
  culture?: string; valveIds: string[];
  duration_minutes: number; overlap_seconds: number;
}
export interface ProgramSchedule {
  startMode: 'now' | 'scheduled'; startDate?: string; startTime?: string;
  recurrence: RecurrenceType; weekDays?: number[]; cycleIntervalDays?: number;
}
export interface Program {
  id: string; name: string; status: string; version: number; farm_id: string;
  timezone: string; schedule: { type: string; local_time: string; local_date?: string; start_local_date?: string; interval_days?: number; weekdays?: string[] }; steps: { binding_id: string; order: number; duration_seconds: number; overlap_to_next_seconds?: number | null }[];
}
export const RECOMMENDED_OVERLAP_SECONDS = 30;
export const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

export function bindingSectors(bindings: ParcelValveBinding[], customNames = new Map<string, string>()): ProgrammableSector[] {
  return bindings.map(b => ({
    id: b.binding_id, name: [b.site, b.sector_code, normalizeCustomDisplayName(customNames.get(sectorNameKey(b.site, `binding:${b.binding_id}`))) ?? b.parcel].filter(Boolean).join(' · '),
    // One canonical parcel binding per step, never a controller/channel.
    valveIds: [], equipmentOk: true,
  }));
}
function positiveInteger(n: number): boolean { return Number.isSafeInteger(n) && n > 0; }
export function schedulePayload(schedule: ProgramSchedule) {
  if (schedule.startMode !== 'scheduled') throw new Error(t("programming:text68"));
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.startTime || '')) throw new Error(t("programming:text69"));
  const local_time = schedule.startTime!;
  const date = schedule.startDate || '';
  if (schedule.recurrence === 'once' || schedule.recurrence === 'cyclic') {
    const parsed = new Date(date + 'T12:00:00Z');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0,10) !== date)
      throw new Error(t("programming:text70"));
  }
  switch (schedule.recurrence) {
    case 'once': return { type: 'ONCE', local_date: date, local_time };
    case 'daily': return { type: 'DAILY', local_time };
    case 'weekly': {
      const days = schedule.weekDays || [];
      if (!days.length || new Set(days).size !== days.length || days.some(d => !Number.isInteger(d) || d < 0 || d > 6))
        throw new Error(t("programming:text71"));
      return { type: 'WEEKLY', weekdays: days.map(d => WEEKDAYS[d]), local_time };
    }
    case 'cyclic':
      if (!positiveInteger(schedule.cycleIntervalDays || 0)) throw new Error(t("programming:text72"));
      return { type: 'CYCLIC', start_local_date: date, interval_days: schedule.cycleIntervalDays, local_time };
    default: throw new Error(t("programming:text73"));
  }
}
export function draftPayload(
  farmId: string, timezone: string, name: string, type: ProgramType,
  steps: ProgramStep[], schedule: ProgramSchedule, available: ProgrammableSector[],
) {
  if (!farmId || !name.trim()) throw new Error(t("programming:text74"));
  try { new Intl.DateTimeFormat(i18n.language, { timeZone: timezone }).format(); }
  catch { throw new Error(t('programming:invalidTimezone')); }
  if (type !== 'irrigation') throw new Error(t("programming:text75"));
  if (!steps.length || new Set(steps.map(s => s.sectorId)).size !== steps.length) throw new Error(t("programming:text76"));
  const allowed = new Set(available.map(s => s.id));
  const canonical = steps.map((s, i) => {
    if (!allowed.has(s.sectorId) || !positiveInteger(s.duration_minutes * 60) ||
        !Number.isSafeInteger(s.overlap_seconds) || s.overlap_seconds < 0) throw new Error(t("programming:text77"));
    return { binding_id: s.sectorId, order: i, duration_seconds: s.duration_minutes * 60,
      overlap_to_next_seconds: i === steps.length - 1 ? 0 : s.overlap_seconds };
  });
  return { farm_id: farmId, name: name.trim(), irrigation_type: 'IRRIGATION', status: 'DRAFT',
    timezone, steps: canonical, schedule: schedulePayload(schedule) };
}
export function computeProgramDuration(steps: ProgramStep[]): number {
  return steps.reduce((sum, s, i) => sum + s.duration_minutes +
    (i < steps.length - 1 ? s.overlap_seconds / 60 : 0), 0);
}
export function formatDuration(minutes: number): string {
  const seconds = Math.round(minutes * 60);
  return [Math.floor(seconds / 3600) ? t('programming:hours', { count: Math.floor(seconds / 3600) }) : '',
    t('programming:minutes', { count: Math.floor(seconds % 3600 / 60) }), seconds % 60 ? t('programming:seconds', { count: seconds % 60 }) : ''].filter(Boolean).join(' ');
}
export function computeTimeline(steps: ProgramStep[], startTime = '06:00') {
  const [h, m] = startTime.split(':').map(Number);
  let offset = 0;
  const label = (minute: number) => {
    const seconds = Math.round((h * 60 + m + minute) * 60);
    const day = Math.floor(seconds / 86400);
    return [Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60]
      .map(n => String(n).padStart(2, '0')).join(':') + (day ? ' ' + t('programming:dayOffset', { day }) : '');
  };
  return steps.map((step, i) => {
    const startMinute = offset, endMinute = offset + step.duration_minutes;
    offset = endMinute + (i < steps.length - 1 ? step.overlap_seconds / 60 : 0);
    return { step, startMinute, endMinute, startLabel: label(startMinute), endLabel: label(endMinute) };
  });
}
export const listPrograms = (farmId: string) =>
  iotApi.getJson<Program[]>('/iot/irrigation-programs?farm_id=' + encodeURIComponent(farmId) + '&status=DRAFT');
export const saveDraft = (payload: ReturnType<typeof draftPayload>, creationRequestId?: string): Promise<Program> =>
  iotApi.post('/iot/irrigation-programs', { ...payload, ...(creationRequestId ? { creation_request_id: creationRequestId } : {}) });

export function programmingApiError(error: unknown): string {
  const code = (error as { semanticCode?: string })?.semanticCode;
  if (code === 'unauthenticated') return t('programming:unauthenticated');
  if (code === 'forbidden' || code === 'not_found') return t('programming:forbidden');
  if (code === 'bad_request' || code === 'validation_error' || code === 'CREATION_REQUEST_CONFLICT') return t('programming:invalidData');
  return t('programming:unavailable');
}
